/* iter-reader-sync-health — Companion ingest sparkline widget.
 *
 * Renders the last 14 days of `notebook_ingest_audit` volume as an
 * inline SVG sparkline plus a red pill if the invalid-note ratio
 * spikes past 5%. Sits on the Admin Companion surface so ops sees a
 * broken Reader release the same day.
 */
import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, Loader2, AlertTriangle } from 'lucide-react';

export default function ReaderSyncHealthWidget() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const d = await adminApi.readerSyncHealth(14);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="rounded-md border border-border p-3" data-testid="reader-sync-health">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-md border border-border p-3 text-[12px] text-destructive" data-testid="reader-sync-health-error">
        {error || 'No data yet.'}
      </div>
    );
  }

  const series = data.series || [];
  const values = series.map(b => b.accepted + b.invalid);
  const max = Math.max(1, ...values);
  const w = 220, h = 40;
  const step = series.length > 1 ? w / (series.length - 1) : 0;
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`).join(' ');

  const invalidRatioPct = (data.invalid_ratio * 100).toFixed(1);
  const alarm = data.invalid_ratio > 0.05;

  return (
    <Card data-testid="reader-sync-health">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-[13px]">Reader Sync Health · 14d</CardTitle>
        <button
          type="button"
          onClick={load}
          className="text-muted-foreground hover:text-foreground"
          data-testid="reader-sync-health-refresh"
          title="Refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </CardHeader>
      <CardContent className="space-y-2">
        <svg width={w} height={h} className="text-primary" viewBox={`0 0 ${w} ${h}`} data-testid="reader-sync-health-sparkline">
          <polyline
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            points={points}
          />
          {values.map((v, i) => (
            <circle key={i} cx={(i * step).toFixed(1)} cy={(h - (v / max) * h).toFixed(1)} r="1.5" fill="currentColor" />
          ))}
        </svg>
        <div className="flex flex-wrap items-center gap-3 text-[11px]">
          <span data-testid="reader-sync-health-accepted">
            <span className="text-muted-foreground">Accepted</span>{' '}
            <b>{data.total_accepted.toLocaleString()}</b>
          </span>
          <span data-testid="reader-sync-health-invalid">
            <span className="text-muted-foreground">Invalid</span>{' '}
            <b>{data.total_invalid.toLocaleString()}</b>
          </span>
          <span data-testid="reader-sync-health-batches">
            <span className="text-muted-foreground">Batches</span>{' '}
            <b>{data.total_batches.toLocaleString()}</b>
          </span>
          <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 ${alarm ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`} data-testid="reader-sync-health-ratio-pill">
            {alarm && <AlertTriangle className="h-3 w-3" />}
            {invalidRatioPct}% invalid
          </span>
        </div>
        {alarm && (
          <p className="text-[11px] text-rose-700" data-testid="reader-sync-health-warning">
            Invalid-note ratio is above 5%. Check the Reader's latest release for
            malformed payloads or oversize <code>body_md</code>.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
